import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { Subject, catchError, debounceTime, of, switchMap } from 'rxjs';
import { WnPerms } from '../../../../../core/auth/worknest-admin-permissions';
import type { AssetChargeInput, AssetChargePreview } from '../../../../../core/models/assets/asset-charges.dto';
import { AssetChargesService } from '../../../../../core/services/asset-charges.service';
import { PermissionService } from '../../../../../core/services/permission.service';
import { AssetChargePreviewComponent } from '../../asset-charges/asset-charge-preview/asset-charge-preview.component';
import { BenefitsDialogComponent } from '../../benefits/shared/dialogs/benefits-dialog/benefits-dialog.component';

export type AssetLossDialogAsset = {
  purchaseCost?: number | null;
  assignedUserId?: string | null;
  assignedUserName?: string | null;
};

const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Confirms marking an asset lost or damaged, with an optional charge to the employee holding it.
 * `confirmed` emits the charge, or `null` when the employee is not charged.
 */
@Component({
  selector: 'app-asset-loss-dialog',
  standalone: true,
  imports: [TranslatePipe, ButtonModule, BenefitsDialogComponent, AssetChargePreviewComponent],
  templateUrl: './asset-loss-dialog.component.html',
  styleUrl: './asset-loss-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetLossDialogComponent implements OnInit {
  private readonly charges = inject(AssetChargesService);
  private readonly permissions = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly previewTrigger = new Subject<void>();

  readonly mode = input.required<'lost' | 'damaged'>();
  readonly asset = input.required<AssetLossDialogAsset>();
  readonly confirmed = output<AssetChargeInput | null>();
  readonly closed = output<void>();

  protected readonly canCharge = computed(() => this.permissions.hasAny(WnPerms.assets.chargeManage));
  protected readonly hasAssignee = computed(() => !!this.asset().assignedUserId);
  protected readonly chargeOn = signal(false);
  protected readonly payment = signal<'full' | 'installments'>('full');
  protected readonly amountText = signal('');
  protected readonly countText = signal('2');
  protected readonly start = signal('');
  protected readonly note = signal('');
  protected readonly preview = signal<AssetChargePreview | null>(null);

  protected readonly installments = computed(() => (this.payment() === 'full' ? 1 : Number(this.countText())));
  protected readonly amount = computed(() => Number(this.amountText()));
  protected readonly chargeValid = computed(() => {
    const count = this.installments();
    return this.amount() > 0 && Number.isInteger(count) && count >= (this.payment() === 'full' ? 1 : 2);
  });
  protected readonly canConfirm = computed(() => !this.chargeOn() || this.chargeValid());

  constructor() {
    this.previewTrigger
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        switchMap(() => {
          const userId = this.asset().assignedUserId;
          if (!this.chargeOn() || !this.chargeValid() || !userId) return of(null);
          return this.charges.preview(userId, this.amount(), this.installments()).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.preview.set(result));
  }

  ngOnInit(): void {
    const cost = this.asset().purchaseCost;
    if (this.mode() === 'lost' && cost != null && cost > 0) this.amountText.set(String(cost));
    this.charges
      .currentPeriod()
      .pipe(catchError(() => of('')), takeUntilDestroyed(this.destroyRef))
      .subscribe((period) => {
        if (!this.start()) this.start.set(period.trim().replace(/^"|"$/g, ''));
      });
  }

  protected toggleCharge(on: boolean): void {
    this.chargeOn.set(on);
    this.queuePreview();
  }

  protected setPayment(value: 'full' | 'installments'): void {
    this.payment.set(value);
    this.queuePreview();
  }

  protected setAmount(value: string): void {
    this.amountText.set(value);
    this.queuePreview();
  }

  protected setCount(value: string): void {
    this.countText.set(value);
    this.queuePreview();
  }

  protected confirm(): void {
    if (!this.canConfirm()) return;
    if (!this.chargeOn()) {
      this.confirmed.emit(null);
      return;
    }
    this.confirmed.emit({
      amount: this.amount(),
      installments: this.installments(),
      startPayrollPeriodKey: this.start() || null,
      note: this.note().trim() || null,
    });
  }

  private queuePreview(): void {
    this.preview.set(null);
    this.previewTrigger.next();
  }
}
